import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Mail, Lock, AlertCircle } from 'lucide-react';
import { COUNTRIES, getStatesForCountry, ASSOCIATIONS } from '../lib/geoData';
import { Seo } from '../components/Seo';
import { GoogleSignInButton } from '../components/GoogleSignInButton';

/**
 * Registration screen — clean EG3 brand chrome (iter114s.68).
 *
 * Replaces the previous "Inscribe Thy Name" medieval theme (knight
 * artwork + Cormorant Garamond italics + bilingual MEDIEVAL/English
 * labels) with the standard EG3 surface used by Splash, Login,
 * Pricing Catalog, and Product Finder — pale-green gradient backdrop,
 * the circular EG3 logo, lucide icons, plain conversational copy.
 *
 * Functional logic (form fields, validation, geo lookups, association
 * picker, newsletter opt-in, register() submit, redirect-on-success)
 * is unchanged. All data-testids are preserved so the existing test
 * suite continues to pass without edits.
 */

const Register = () => {
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    company: '',
    country: '',
    state_region: '',
    password: '',
    confirmPassword: '',
    association_membership_number: '',
    association: '',
    newsletter_signup: false,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords don't match. Please try again.");
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    setLoading(true);

    const { confirmPassword: _confirm, ...userData } = formData;
    const result = await register(userData);

    if (result.success) {
      navigate('/splash');
    } else {
      const msg = result.error || '';
      const isDuplicate = /already registered|already exists/i.test(msg);
      setError(
        isDuplicate
          ? 'This email is already registered. Try signing in instead.'
          : msg || 'An error occurred. Please try again.',
      );
    }

    setLoading(false);
  };

  const inputClass =
    'w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6FBF73] focus:border-transparent text-base';
  const inputWithIconClass =
    'w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6FBF73] focus:border-transparent text-base';
  const labelClass =
    'block text-sm font-medium text-[#14281D] mb-1.5';

  const stateOptions = getStatesForCountry(formData.country);

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-[#E8F1E6] via-[#F4F7F2] to-white flex items-center justify-center px-4 py-10"
      data-testid="register-page-medieval"
    >
      <Seo
        title="Create Account | Evergreen Graphics Group"
        description="Create your Evergreen Graphics Group account to save quotes, track orders and approve proofs."
        path="/register"
        noindex
      />
      <div className="w-full max-w-2xl">
        {/* Logo + title */}
        <div className="text-center mb-6">
          <Link to="/splash" data-testid="register-logo-link" className="inline-block">
            <img
              src="/eg3-logo.png"
              alt="Evergreen Graphics Group"
              className="h-20 w-20 mx-auto mb-3 rounded-full ring-2 ring-[#6FBF73]/30 object-contain bg-white"
            />
          </Link>
          <h1
            className="text-3xl md:text-4xl font-bold text-[#14281D] tracking-tight"
            data-testid="register-heading"
          >
            Create your account
          </h1>
          <p
            className="mt-2 text-sm md:text-base text-[#1F3D2C]/75"
            data-testid="register-subheading"
          >
            Set up your Evergreen Graphics account to manage orders, save drafts, and unlock VIP pricing.
          </p>
        </div>

        {/* Form card */}
        <div
          className="bg-white rounded-2xl shadow-xl p-7 border border-[#E5E7EB]"
          data-testid="register-form-card"
        >
          {error && (
            <div
              className="mb-5 p-3.5 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 text-red-700 text-sm"
              data-testid="register-error-banner"
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="reg-email" className={labelClass} data-testid="register-email-label">
                Email <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                <input
                  id="reg-email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                  className={inputWithIconClass}
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label htmlFor="reg-first" className={labelClass} data-testid="register-firstname-label">
                  First name <span className="text-red-500">*</span>
                </label>
                <input
                  id="reg-first"
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  placeholder="First name"
                  required
                  autoComplete="given-name"
                  className={inputClass}
                  data-testid="register-firstname-input"
                />
              </div>
              <div>
                <label htmlFor="reg-last" className={labelClass} data-testid="register-lastname-label">
                  Last name <span className="text-red-500">*</span>
                </label>
                <input
                  id="reg-last"
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  placeholder="Last name"
                  required
                  autoComplete="family-name"
                  className={inputClass}
                  data-testid="register-lastname-input"
                />
              </div>
            </div>

            <div>
              <label htmlFor="reg-company" className={labelClass} data-testid="register-company-label">
                Company <span className="text-gray-400 text-xs">(optional)</span>
              </label>
              <input
                id="reg-company"
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder="Your company or organization"
                className={inputClass}
                data-testid="register-company-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label htmlFor="reg-country" className={labelClass} data-testid="register-country-label">
                  Country <span className="text-red-500">*</span>
                </label>
                <select
                  id="reg-country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  required
                  className={inputClass}
                  data-testid="register-country-select"
                >
                  <option value="">Select a country</option>
                  {COUNTRIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="reg-region" className={labelClass} data-testid="register-region-label">
                  State / Province <span className="text-red-500">*</span>
                </label>
                {stateOptions ? (
                  <select
                    id="reg-region"
                    name="state_region"
                    value={formData.state_region}
                    onChange={handleChange}
                    required
                    className={inputClass}
                    data-testid="register-region-select"
                  >
                    <option value="">Select state / province</option>
                    {stateOptions.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    id="reg-region"
                    type="text"
                    name="state_region"
                    value={formData.state_region}
                    onChange={handleChange}
                    placeholder="State, province, or region"
                    required
                    className={inputClass}
                    data-testid="register-region-input"
                  />
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label htmlFor="reg-pw" className={labelClass} data-testid="register-password-label">
                  Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                  <input
                    id="reg-pw"
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="At least 6 characters"
                    required
                    autoComplete="new-password"
                    className={inputWithIconClass}
                    data-testid="register-password-input"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="reg-pw2" className={labelClass} data-testid="register-confirm-password-label">
                  Confirm password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                  <input
                    id="reg-pw2"
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Re-enter your password"
                    required
                    autoComplete="new-password"
                    className={inputWithIconClass}
                    data-testid="register-confirm-password-input"
                  />
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="reg-assoc-no" className={labelClass} data-testid="register-association-number-label">
                Association membership number <span className="text-gray-400 text-xs">(optional)</span>
              </label>
              <input
                id="reg-assoc-no"
                type="text"
                name="association_membership_number"
                value={formData.association_membership_number}
                onChange={handleChange}
                placeholder="e.g. 123456"
                className={inputClass}
                data-testid="register-association-number-input"
              />
              <p
                className="mt-1.5 text-xs text-gray-500 leading-snug"
                data-testid="register-association-number-help"
              >
                A unique ID assigned to a person or company when joining an organization, trade group, club, or professional association.
              </p>
            </div>

            <div>
              <label htmlFor="reg-assoc" className={labelClass} data-testid="register-association-label">
                Association <span className="text-gray-400 text-xs">(optional)</span>
              </label>
              <select
                id="reg-assoc"
                name="association"
                value={formData.association}
                onChange={handleChange}
                className={inputClass}
                data-testid="register-association-select"
              >
                <option value="">Please select</option>
                {ASSOCIATIONS.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="flex items-start cursor-pointer gap-2" data-testid="register-newsletter-label">
                <input
                  type="checkbox"
                  name="newsletter_signup"
                  checked={formData.newsletter_signup}
                  onChange={handleChange}
                  className="w-4 h-4 mt-1 text-[#6FBF73] border-gray-300 rounded focus:ring-[#6FBF73] accent-[#6FBF73]"
                  data-testid="register-newsletter-checkbox"
                />
                <span className="text-sm text-[#14281D] leading-snug">
                  Send me newsletter & promotions from Evergreen Graphics
                </span>
              </label>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#6FBF73] hover:bg-[#4A7C59] text-white py-3 text-base font-semibold rounded-full transition-colors"
              data-testid="register-submit-button"
            >
              {loading ? 'Creating account…' : 'Create account'}
            </Button>
          </form>

          <div className="my-5 flex items-center gap-3" data-testid="register-or-divider">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-gray-400 font-medium">OR</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>
          <GoogleSignInButton label="Sign up with Google" />

          <div className="mt-5 text-center text-sm text-[#1F3D2C]/75">
            Already have an account?{' '}
            <Link
              to="/login"
              className="text-[#2F5D3A] hover:text-[#6FBF73] hover:underline font-semibold"
              data-testid="register-login-link"
            >
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
